import {
  Content,
  Group,
  Item,
  ItemIndicator,
  Label,
  Portal,
  RadioGroup,
  RadioItem,
  Root,
  Separator,
  Sub,
  SubContent,
  SubTrigger,
  Trigger,
} from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import {
  type ComponentProps,
  type CSSProperties,
  createContext,
  forwardRef,
  isValidElement,
  type ReactNode,
  useContext,
} from 'react';

import { UIPortalContext } from '../ui-theme/UITheme';
import styles from './Menu.module.css';

export interface MenuProps {
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** False lets focus leave the open menu (Radix's `modal`); default true. */
  modal?: boolean;
}

/** Dropdown menu built on Radix. Compose: `Menu` > `MenuButton` (the trigger) +
 *  `MenuList` > `MenuItem`/`MenuGroup`/`MenuDivider`. */
export const Menu = ({
  children,
  open,
  defaultOpen,
  onOpenChange,
  modal,
  ...props
}: MenuProps) => (
  <Root
    open={open}
    defaultOpen={defaultOpen}
    onOpenChange={onOpenChange}
    modal={modal}
    {...props}
  >
    {children}
  </Root>
);

export interface MenuButtonProps {
  /** A single trigger element (e.g. a Button/IconButton that forwards a ref). */
  children: ReactNode;
  asChild?: boolean;
  className?: string;
  disabled?: boolean;
}

/** The element that opens the menu. Wraps a single ref-forwarding child. */
export const MenuButton = ({
  children,
  asChild = true,
  className,
  disabled,
}: MenuButtonProps) => {
  const child = isValidElement<{ disabled?: boolean; loading?: boolean }>(
    children
  )
    ? children
    : null;
  // Radix opens on pointerdown, which browsers still fire on a disabled button.
  const isDisabled =
    disabled ?? Boolean(child?.props.disabled || child?.props.loading);
  return (
    <Trigger asChild={asChild} className={className} disabled={isDisabled}>
      {children}
    </Trigger>
  );
};

type MenuSize = 'md' | 'sm';

const toLength = (value: number | string) =>
  typeof value === 'number' ? `${value}px` : value;

/** Radix exposes the room left between the anchor and the viewport edge on
 *  the content element, so a caller's cap never pushes the surface
 *  off-screen. */
const surfaceStyle = (
  style: CSSProperties | undefined,
  minWidth: number | string | undefined,
  maxHeight: number | string | undefined
): CSSProperties | undefined => {
  if (minWidth === undefined && maxHeight === undefined) return style;
  return {
    ...style,
    ...(minWidth !== undefined && { minWidth }),
    ...(maxHeight !== undefined && {
      maxHeight: `min(${toLength(maxHeight)}, var(--radix-dropdown-menu-content-available-height))`,
    }),
  };
};

export interface MenuListProps
  extends Omit<ComponentProps<typeof Content>, 'asChild'> {
  children: ReactNode;
  portal?: boolean;
  /** Min width of the menu surface; overrides the default 10rem floor. */
  minWidth?: number | string;
  /** Caps the surface height; the items scroll past it. */
  maxHeight?: number | string;
  size?: MenuSize;
}

export const MenuList = ({
  children,
  className,
  portal = true,
  minWidth,
  maxHeight,
  size = 'md',
  style,
  ...props
}: MenuListProps) => {
  const portalTarget = useContext(UIPortalContext);
  const content = (
    <Content
      className={classNames(
        styles.content,
        maxHeight !== undefined && styles.scrollable,
        className
      )}
      data-size={size}
      sideOffset={4}
      align="start"
      style={surfaceStyle(style, minWidth, maxHeight)}
      {...props}
    >
      {children}
    </Content>
  );

  return portal ? <Portal container={portalTarget}>{content}</Portal> : content;
};

export interface MenuItemProps
  extends Omit<
    ComponentProps<typeof Item>,
    'onSelect' | 'asChild' | 'onClick'
  > {
  /** Fires on click and keyboard select. Receives the Radix select event, so
   *  callers can `event.preventDefault()` to keep the menu open. */
  onClick?: (event: Event) => void;
  icon?: ReactNode;
  /** Right-aligned secondary text, e.g. a keyboard shortcut. */
  trailing?: ReactNode;
  href?: string;
  destructive?: boolean;
}

/** A menu action. forwardRef + prop spread so it composes as a Tooltip/Trigger
 *  `asChild` target. */
export const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(
  function MenuItem(
    {
      children,
      onClick,
      disabled,
      icon,
      trailing,
      className,
      href,
      destructive = false,
      ...rest
    },
    ref
  ) {
    const content = (
      <>
        {icon ? (
          <span className={styles.itemIcon} aria-hidden="true">
            {icon}
          </span>
        ) : null}
        {children}
        {trailing ? (
          <span className={styles.itemTrailing}>{trailing}</span>
        ) : null}
      </>
    );
    return (
      <Item
        ref={ref}
        className={classNames(
          styles.item,
          destructive && styles.itemDestructive,
          className
        )}
        disabled={disabled}
        onSelect={onClick}
        asChild={href !== undefined}
        {...rest}
      >
        {href !== undefined ? (
          <a href={disabled ? undefined : href}>{content}</a>
        ) : (
          content
        )}
      </Item>
    );
  }
);

type MenuRadioIndicator = 'check' | 'none';

const MenuRadioIndicatorContext = createContext<MenuRadioIndicator>('check');

export interface MenuRadioGroupProps {
  children: ReactNode;
  value?: string;
  onValueChange?: (value: string) => void;
  label?: ReactNode;
  className?: string;
  /** `none` drops the tick: the items keep their radio semantics and the
   *  caller styles the checked one via `[data-state="checked"]`. */
  indicator?: MenuRadioIndicator;
}

export const MenuRadioGroup = ({
  children,
  value,
  onValueChange,
  label,
  className,
  indicator = 'check',
}: MenuRadioGroupProps) => (
  <RadioGroup value={value} onValueChange={onValueChange} className={className}>
    {label ? <Label className={styles.groupLabel}>{label}</Label> : null}
    <MenuRadioIndicatorContext.Provider value={indicator}>
      {children}
    </MenuRadioIndicatorContext.Provider>
  </RadioGroup>
);

export type MenuRadioItemProps = Omit<
  ComponentProps<typeof RadioItem>,
  'asChild'
>;

export const MenuRadioItem = forwardRef<HTMLDivElement, MenuRadioItemProps>(
  function MenuRadioItem({ children, className, ...rest }, ref) {
    const indicator = useContext(MenuRadioIndicatorContext);
    return (
      <RadioItem
        ref={ref}
        className={classNames(styles.item, className)}
        {...rest}
      >
        {indicator === 'check' ? (
          <span className={styles.itemIndicatorSlot} aria-hidden="true">
            <ItemIndicator>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m5 12 5 5 9-10" />
              </svg>
            </ItemIndicator>
          </span>
        ) : null}
        {children}
      </RadioItem>
    );
  }
);

export interface MenuSubProps {
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Nested submenu. Compose: `MenuSub` > `MenuSubTrigger` + `MenuSubContent`. */
export const MenuSub = ({ children, ...props }: MenuSubProps) => (
  <Sub {...props}>{children}</Sub>
);

export interface MenuSubTriggerProps
  extends Omit<ComponentProps<typeof SubTrigger>, 'asChild'> {
  icon?: ReactNode;
  /** Trailing content shown before the chevron (e.g. the current value). */
  trailing?: ReactNode;
}

/** Row that opens a submenu on hover/select. Mirrors `MenuItem`'s layout with a
 *  trailing value slot and a chevron affordance. */
export const MenuSubTrigger = forwardRef<HTMLDivElement, MenuSubTriggerProps>(
  function MenuSubTrigger(
    { children, icon, trailing, className, ...rest },
    ref
  ) {
    return (
      <SubTrigger
        ref={ref}
        className={classNames(styles.item, styles.subTrigger, className)}
        {...rest}
      >
        {icon ? (
          <span className={styles.itemIcon} aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <span className={styles.subTriggerLabel}>{children}</span>
        {trailing ? (
          <span className={styles.subTriggerValue}>{trailing}</span>
        ) : null}
        <svg
          className={styles.subTriggerChevron}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m9 6 6 6-6 6" />
        </svg>
      </SubTrigger>
    );
  }
);

export interface MenuSubContentProps
  extends Omit<ComponentProps<typeof SubContent>, 'asChild'> {
  children: ReactNode;
  portal?: boolean;
  /** Caps the surface height; the items scroll past it. */
  maxHeight?: number | string;
  size?: MenuSize;
}

/** Floating panel holding a submenu's items. Portalled by default, mirroring
 *  `MenuList`. */
export const MenuSubContent = ({
  children,
  className,
  portal = true,
  maxHeight,
  size = 'md',
  style,
  ...props
}: MenuSubContentProps) => {
  const portalTarget = useContext(UIPortalContext);
  const content = (
    <SubContent
      className={classNames(
        styles.content,
        maxHeight !== undefined && styles.scrollable,
        className
      )}
      data-size={size}
      style={surfaceStyle(style, undefined, maxHeight)}
      sideOffset={4}
      alignOffset={-4}
      {...props}
    >
      {children}
    </SubContent>
  );

  return portal ? <Portal container={portalTarget}>{content}</Portal> : content;
};

export interface MenuGroupProps {
  children: ReactNode;
  label?: ReactNode;
  className?: string;
}

export const MenuGroup = ({ children, label, className }: MenuGroupProps) => (
  <Group className={className}>
    {label ? <Label className={styles.groupLabel}>{label}</Label> : null}
    {children}
  </Group>
);

export const MenuDivider = ({ className }: { className?: string }) => (
  <Separator className={classNames(styles.divider, className)} />
);
